import clsx from 'clsx'
import React, { ReactNode } from 'react'

import { SliderWithArrowsAndIndicator } from '@Components/Slider/SliderWithArrowsAndIndicator'
import { TileSettings } from '@Components/Tiles/settings'

import styles from './Tiles.module.scss'
import v2Styles from './TilesV2.module.scss'

const generateStyleVars = (rowsMobile: number | string, rowsDesktop: number | string): React.CSSProperties => {
  const styleVars: React.CSSProperties = {}

  if (typeof rowsMobile === 'string' && rowsMobile !== 'auto') {
    styleVars['--grid-template-rows' as any] = `repeat(${rowsMobile}, 1fr)`
  } else if (typeof rowsMobile === 'number' && rowsMobile !== 1) {
    styleVars['--grid-template-rows' as any] = `repeat(${rowsMobile}, 1fr)`
  }

  if (typeof rowsDesktop === 'string' && rowsDesktop !== 'auto') {
    styleVars['--grid-template-rows-desktop' as any] = `repeat(${rowsDesktop}, 1fr)`
  } else if (typeof rowsDesktop === 'number' && rowsDesktop !== 1) {
    styleVars['--grid-template-rows-desktop' as any] = `repeat(${rowsDesktop}, 1fr)`
  }

  return styleVars
}

const ItemsContainer = ({
  children,
  settings,
}: {
  children: React.ReactNode
  settings: TileSettings
}) => {
  const shouldUseColumnFlowMobile = settings.rowsMobile !== 'auto' && settings.rowsMobile !== 1
  const shouldUseColumnFlowDesktop = settings.rowsDesktop !== 'auto' && settings.rowsDesktop !== 1
  const styleVars = generateStyleVars(settings.rowsMobile, settings.rowsDesktop)

  if (settings.version === '2') {
    // An explicit `columns` count is passed to the CSS as --tile-cols and rendered by a single
    // `fixedCols` rule (see TilesV2.module.scss); 'auto' keeps the fluid auto-fill default.
    const hasFixedColumns = settings.columns !== 'auto'
    const columnVars = hasFixedColumns
      ? ({ '--tile-cols': settings.columns } as React.CSSProperties)
      : undefined

    if (settings.enableSlider) {
      return (
        <SliderWithArrowsAndIndicator
          hideArrows
          sliderClassName={clsx(
            v2Styles.slider,
            hasFixedColumns && v2Styles.fixedCols,
            shouldUseColumnFlowMobile && v2Styles.columnFlowMobile,
            shouldUseColumnFlowDesktop && v2Styles.columnFlowDesktop,
          )}
          style={{ ...styleVars, ...columnVars }}
        >
          {children}
        </SliderWithArrowsAndIndicator>
      )
    }

    return (
      <div
        className={clsx(
          v2Styles.list,
          hasFixedColumns && v2Styles.fixedCols,
          settings.stretch && v2Styles.stretch,
        )}
        style={columnVars}
      >
        {children}
      </div>
    )
  }

  if (settings.enableSlider) {
    return (
      <SliderWithArrowsAndIndicator
        hideArrows
        sliderClassName={clsx(
          styles.slider,
          shouldUseColumnFlowMobile && styles.columnFlowMobile,
          shouldUseColumnFlowDesktop && styles.columnFlowDesktop,
          styles[settings.cardSize],
        )}
        style={styleVars}
      >
        {children}
      </SliderWithArrowsAndIndicator>
    )
  }

  return (
    <div
      className={clsx(
        styles.list,
        shouldUseColumnFlowMobile && styles.columnFlowMobile,
        shouldUseColumnFlowDesktop && styles.columnFlowDesktop,
        styles[settings.cardSize],
      )}
      style={styleVars}
    >
      {children}
    </div>
  )
}

export const Tiles = ({
  children,
  settings,
}: {
  children: ReactNode
  settings: TileSettings
}) => {
  if (settings.version === '2') {
    return (
      <div className={clsx(v2Styles.container, v2Styles[settings.size])}>
        <ItemsContainer settings={settings}>
          {children}
        </ItemsContainer>
      </div>
    )
  }

  return (
    <div className={clsx(styles.container, settings.autoFit && styles.autoFit)}>
      <ItemsContainer settings={settings}>
        {children}
      </ItemsContainer>
    </div>
  )
}

